import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  CheckCircle,
  PictureAsPdf,
  TableChart,
  Description,
  Clear
} from '@mui/icons-material';
import { FaAmazon, FaWarehouse, FaBarcode } from 'react-icons/fa';
import { useActivityLog } from '../context/ActivityLogContext';
import { API_ENDPOINTS, apiCall } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const PDFtoExcel = () => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setActivityLogs,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFilePath, setSelectedFilePath] = useState('');
  const [labelType, setLabelType] = useState('Amazon');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState(null);
  const [processId, setProcessId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const file = droppedFiles[0];
    if (file && file.name.toLowerCase().endsWith('.pdf')) {
      setSelectedFile(file);
      setSelectedFilePath(file.name);
      setError('');
      setSuccess('');
    } else {
      setError('Please drop a PDF file only.');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setError('Please select a PDF file only.');
        return;
      }
      setSelectedFile(file);
      setSelectedFilePath(file.name);
      setError('');
      setSuccess('');
    }
  };

  // Fetch status for processing
  const fetchStatus = async () => {
    const currentProcessId = processIdRef.current;
    if (!currentProcessId) return;

    try {
      const statusData = await apiCall(API_ENDPOINTS.PDF_TO_EXCEL_STATUS(currentProcessId), {
        method: 'GET',
      });

      if (statusData) {
        const progressPct = statusData.progressPercentage ?? 0;
        const totalItems = statusData.totalItems ?? 0;
        const processedItems = statusData.processedItems ?? 0;
        const logs = statusData.logs ?? [];
        const currentStatus = statusData.status ?? '';

        // Update global activity log
        updateActivityProcess(currentProcessId, {
          progressPercentage: progressPct,
          totalItems: totalItems,
          processedItems: processedItems,
        });

        // Update logs in global context
        if (logs && logs.length > 0) {
          setActivityLogs(currentProcessId, logs);
        }

        // Check if processing is complete
        if (currentStatus === 'completed' || currentStatus === 'error') {
          // Clear interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          clearIntervalRef(currentProcessId);

          // Complete the activity process
          completeActivityProcess(currentProcessId, currentStatus === 'completed');

          if (currentStatus === 'completed') {
            // Auto-download the file
            if (!downloadTriggeredRef.current) {
              downloadTriggeredRef.current = true;
              await downloadFile(currentProcessId, statusData.fileName);
            }

            setSuccess(`${labelType} labels successfully converted to Excel format and downloaded!`);
            setResults({
              success: true,
              labelType: labelType,
              filename: statusData.fileName,
              processId: currentProcessId
            });
          } else {
            setError('Processing failed. Check the activity log for details.');
          }

          setProcessing(false);
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const downloadFile = async (downloadProcessId, fileName) => {
    try {
      const response = await apiCall(API_ENDPOINTS.PDF_TO_EXCEL_DOWNLOAD(downloadProcessId));

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `${labelType}_Labels.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handleConvert = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file first.');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');
    setResults(null);
    downloadTriggeredRef.current = false;

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('labelType', labelType);

      const token = localStorage.getItem('jwtToken');

      const response = await fetch(API_ENDPOINTS.PDF_TO_EXCEL_CONVERT, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = 'Conversion failed';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } else {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
        } catch (parseError) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.processId) {
        setProcessId(result.processId);
        processIdRef.current = result.processId;

        // Add to global activity log
        addActivityProcess(result.processId, `PDF to Excel (${labelType}): ${selectedFile.name}`, 'PDF to Excel');

        // Start polling for status
        intervalRef.current = setInterval(() => {
          fetchStatus();
        }, 1000);
        setIntervalRef(result.processId, intervalRef.current);
      } else {
        setError('Failed to start processing');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Conversion error:', err);
      setError(err.message || 'An error occurred during conversion. Please try again.');
      setProcessing(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const resetForm = () => {
    setSelectedFile(null);
    setSelectedFilePath('');
    setLabelType('Amazon');
    setError('');
    setSuccess('');
    setResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getLabelDescription = () => {
    if (labelType === 'Amazon') {
      return 'Extract data from Amazon UCC labels including ASIN, UPC, PO#, and shipping information';
    } else {
      return 'Extract data from Wayfair UCC labels including SSCC, Part#, UPC#, and case pack information';
    }
  };

  const getExpectedColumns = () => {
    if (labelType === 'Amazon') {
      return ['ShipTo', 'PO#', 'Style', 'ASIN#', 'UPC', 'Quantity', 'Carton#', 'Country of Origin', 'SSCC'];
    } else {
      return ['SSCC', 'Ship From', 'PO#', 'Part#', 'UPC#', 'Case Pack Quantity', 'Ship Via', 'Carton'];
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="PDF to Excel (UCC Labels)" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          PDF TO EXCEL (UCC LABELS)
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Label Conversion
              </Typography>

              {/* Label Type Selection */}
              <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
                <CardContent>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaBarcode size={16} color={TOOL_COLORS.PDF.primary} />
                        Select Label Type
                      </Box>
                    </FormLabel>
                    <RadioGroup
                      value={labelType}
                      onChange={(e) => setLabelType(e.target.value)}
                      row
                      sx={{ mt: 1 }}
                    >
                      <FormControlLabel
                        value="Amazon"
                        control={<Radio sx={{ color: '#FF9500', '&.Mui-checked': { color: '#FF9500' } }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FaAmazon size={20} color="#FF9500" />
                            <span>Amazon Labels</span>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="Wayfair"
                        control={<Radio sx={{ color: '#7B46C7', '&.Mui-checked': { color: '#7B46C7' } }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FaWarehouse size={20} color="#7B46C7" />
                            <span>Wayfair Labels</span>
                          </Box>
                        }
                      />
                    </RadioGroup>
                    <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                      {getLabelDescription()}
                    </Typography>
                  </FormControl>
                </CardContent>
              </Card>

              {/* File Upload */}
              <Card sx={{ mb: 3, bgcolor: selectedFile ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <PictureAsPdf sx={{ color: TOOL_COLORS.PDF.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      PDF File (UCC Labels)
                    </Typography>
                    {selectedFile && (
                      <CheckCircle color="success" />
                    )}
                  </Box>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".pdf"
                    style={{ display: 'none' }}
                  />

                  {/* Drag and Drop Zone */}
                  <Box
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: `2px dashed ${isDragging ? TOOL_COLORS.PDF.primary : selectedFile ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: isDragging
                        ? TOOL_COLORS.PDF.light
                        : selectedFile
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      '&:hover': {
                        borderColor: selectedFile ? '#4CAF50' : TOOL_COLORS.PDF.primary,
                        bgcolor: selectedFile
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.08)')
                          : TOOL_COLORS.PDF.light,
                      },
                    }}
                  >
                    {selectedFile ? (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          1 file selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click to replace
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.PDF.primary : theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {isDragging ? 'Drop PDF file here' : 'Drag & drop PDF file here or click to select'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PDF files only (.pdf)
                        </Typography>
                      </>
                    )}
                  </Box>
                  {selectedFile && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: TOOL_COLORS.PDF.primary, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setSelectedFilePath(''); }}
                        disabled={processing}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                </CardContent>
              </Card>

              {/* Process Button */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleConvert}
                  disabled={!selectedFile || processing}
                  sx={{
                    bgcolor: TOOL_COLORS.PDF.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.PDF.hover },
                    px: 4
                  }}
                >
                  {processing ? 'Converting...' : 'Convert to Excel'}
                </Button>

                {(selectedFile || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Download Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {/* Progress */}
              {processing && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Converting {labelType} PDF labels to Excel...
                  </Typography>
                  <LinearProgress color="primary" />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Conversion Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <PictureAsPdf color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="PDF Processed"
                        secondary={`${labelType} UCC labels extracted`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <TableChart color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Excel Generated"
                        secondary="Data exported to .xlsx format"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="File Downloaded"
                        secondary={results.filename}
                      />
                    </ListItem>
                  </List>
                </Box>
              )}

              {/* Download Section - File auto-downloads after generation */}
              {results && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Excel file has been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Expected File Format */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Extracted Columns:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                {labelType} label data:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                {getExpectedColumns().map((column, index) => (
                  <Chip
                    key={index}
                    label={column}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                ))}
              </Box>

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Select label type (Amazon or Wayfair)<br />
                2. Upload PDF file with UCC labels<br />
                3. Click "Convert to Excel" to extract data<br />
                4. Excel file downloads automatically<br /><br />
                <strong>Note:</strong> Each page in the PDF is treated as one label and extracted to a row in Excel
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default PDFtoExcel;
